import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  NamedLink,
  AspectRatioWrapper,
  H2,
} from '../../../components';
import img1 from '../SignupForm/images/sleek-doodle.svg';

import css from './LoginForm.module.css';

const LoginFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        intl,
        invalid,
        values,
        errors,
        loginOrSignupError,
      } = fieldRenderProps;

      // email
      const emailLabel = intl.formatMessage({
        id: 'LoginForm.emailLabel',
      });
      const emailPlaceholder = intl.formatMessage({
        id: 'LoginForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'LoginForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'LoginForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      // password
      const passwordLabel = intl.formatMessage({
        id: 'LoginForm.passwordLabel',
      });
      const passwordPlaceholder = intl.formatMessage({
        id: 'LoginForm.passwordPlaceholder',
      });
      const passwordRequiredMessage = intl.formatMessage({
        id: 'LoginForm.passwordRequired',
      });
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      const passwordRecoveryLink = (
        <NamedLink
          name="PasswordRecoveryPage"
          className={css.recoveryLink}
          to={{
            search:
              values?.email && !errors?.email ? `email=${encodeURIComponent(values.email)}` : '',
          }}
        >
          <FormattedMessage id="LoginForm.forgotPassword" />
        </NamedLink>
      );

      const signupLink = (
        <NamedLink name="SignupPage" className={css.link}>
          <FormattedMessage id="LoginForm.SignupLinkText" />
        </NamedLink>
      );

      const signupLinkTutor = (
        <NamedLink name="SignupForUserTypePage" params={{ userType: 'tutor' }} className={css.link}>
          <FormattedMessage id="LoginForm.SignupLinkTutorText" />
        </NamedLink>
      );

      const linksLabel = <FormattedMessage id="LoginForm.linksLabel" />;
      const or = <FormattedMessage id="SignupForm.or" />;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.container}>
            <div className={css.left}>
              <AspectRatioWrapper className={css.aspectRatioWrapper}>
                <img className={css.rootForImage} alt="doodle" src={img1} />
              </AspectRatioWrapper>
            </div>
            <div className={css.right}>
              <H2 className={css.title}>
                <FormattedMessage id="LoginForm.login" />
              </H2>
              <div>
                <FieldTextInput
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  label={emailLabel}
                  placeholder={emailPlaceholder}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                />
                <FieldTextInput
                  className={css.password}
                  type="password"
                  id={formId ? `${formId}.password` : 'password'}
                  name="password"
                  autoComplete="current-password"
                  label={passwordLabel}
                  placeholder={passwordPlaceholder}
                  validate={passwordRequired}
                />
              </div>
              <div className={css.bottomWrapper}>
                <p className={css.bottomWrapperText}>
                  <span className={css.recoveryLinkInfo}>
                    <FormattedMessage
                      id="LoginForm.forgotPasswordInfo"
                      values={{ passwordRecoveryLink }}
                    />
                  </span>
                </p>
                {loginOrSignupError}
                <PrimaryButton
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                >
                  <FormattedMessage id="LoginForm.logIn" />
                </PrimaryButton>
              </div>
              <p className={css.links}>
                <FormattedMessage
                  id="LoginForm.signupLink"
                  values={{ linksLabel: linksLabel, link: signupLink, signupLinkTutor, or: or }}
                />
              </p>
            </div>
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the login form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @returns {JSX.Element}
 */
const LoginForm = props => {
  const intl = useIntl();
  return <LoginFormComponent {...props} intl={intl} />;
};

export default LoginForm;
